import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Tabs,
  Tab,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

const DEFAULT_RADIUS = 30;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split a free-text blob (commas, spaces, semicolons, or new lines) into
 * unique valid / invalid email buckets. Case-insensitive de-duplication.
 */
const parseEmails = (raw) => {
  const tokens = (raw || '')
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const valid = [];
  const invalid = [];
  const seen = new Set();
  for (const token of tokens) {
    const email = token.toLowerCase();
    if (seen.has(email)) continue;
    seen.add(email);
    if (EMAIL_RE.test(email)) valid.push(email);
    else invalid.push(token);
  }
  return { valid, invalid };
};

/**
 * Admin dialog: send an outreach/marketing email to the customer base —
 * the whole active client list, or only clients within a radius of an area.
 * Customer-facing sibling of MaidBroadcastDialog, backed by
 * /api/notifications/clients/*.
 */
const ClientOutreachDialog = ({
  open,
  onClose,
  initialManualEmails = null,
  defaultAudienceMode = null,
}) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  const [activeTab, setActiveTab] = useState(0);

  // 'all' | 'address' | 'technician' | 'manual'
  const [audienceMode, setAudienceMode] = useState('all');
  const [manualInput, setManualInput] = useState('');
  const [address, setAddress] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(DEFAULT_RADIUS);
  const [selectedTech, setSelectedTech] = useState(null);
  const [techInput, setTechInput] = useState('');
  const [techOptions, setTechOptions] = useState([]);
  const [techLoading, setTechLoading] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  // Company BCC mode: 'off' | 'first' | 'all'
  const [bccMode, setBccMode] = useState('first');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [previewMeta, setPreviewMeta] = useState(null);
  const [sendResults, setSendResults] = useState(null);

  // History state
  const [campaigns, setCampaigns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [previewCampaign, setPreviewCampaign] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  const limitToArea = audienceMode === 'address' || audienceMode === 'technician';
  const isManual = audienceMode === 'manual';
  const { valid: manualValid, invalid: manualInvalid } = parseEmails(manualInput);

  const targetBody = () => {
    if (audienceMode === 'address') return { address, radiusMiles: Number(radiusMiles) };
    if (audienceMode === 'technician')
      return { maidUserId: selectedTech?.userId, radiusMiles: Number(radiusMiles) };
    return {};
  };

  // Prefill a manual recipient list when opened from another surface (e.g. the
  // customer map's "Start outreach email" action). Optional props — the
  // dashboard usage passes neither and is unaffected.
  useEffect(() => {
    if (open && initialManualEmails) {
      setAudienceMode(defaultAudienceMode || 'manual');
      setManualInput(
        Array.isArray(initialManualEmails) ? initialManualEmails.join(', ') : initialManualEmails
      );
    }
  }, [open, initialManualEmails, defaultAudienceMode]);

  // Technician search for the picker (active technicians, matched by name/email)
  useEffect(() => {
    if (audienceMode !== 'technician') return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setTechLoading(true);
      try {
        const params = new URLSearchParams({
          activeOnly: 'true',
          limit: '20',
          ...(techInput.trim() ? { search: techInput.trim() } : {}),
        });
        const response = await fetch(`${apiBase}/api/maids/light?${params}`, {
          headers: authHeaders(),
        });
        const result = await response.json();
        if (!cancelled) setTechOptions(result.data || []);
      } catch {
        if (!cancelled) setTechOptions([]);
      } finally {
        if (!cancelled) setTechLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [audienceMode, techInput]);

  // Recipient selection (checkbox list)
  const toggleRecipient = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSelected = recipients.length > 0 && recipients.every((r) => selectedIds.has(r.id));
  const someSelected = recipients.some((r) => selectedIds.has(r.id));
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(recipients.map((r) => r.id)));
  };
  const selectedRecipients = recipients.filter((r) => selectedIds.has(r.id));
  const selectedEmailable = selectedRecipients.filter((r) => r.hasEmail).length;

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const fetchCampaigns = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await fetch(`${apiBase}/api/notifications/clients/campaigns?limit=100`, {
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load history');
      }
      setCampaigns(result.data.campaigns || []);
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && activeTab === 1) {
      fetchCampaigns();
    }
  }, [open, activeTab, fetchCampaigns]);

  const handlePreview = async () => {
    resetMessages();
    setSendResults(null);

    // Manual list: parse and preview locally — no client records to look up.
    if (isManual) {
      const { valid, invalid } = parseEmails(manualInput);
      if (valid.length === 0) {
        setError('Enter at least one valid email address.');
        return;
      }
      const recs = valid.map((email) => ({
        id: email,
        firstName: '',
        lastName: '',
        email,
        hasEmail: true,
        distance: null,
      }));
      setRecipients(recs);
      setSelectedIds(new Set(recs.map((r) => r.id)));
      setPreviewMeta({ totalTargeted: valid.length, emailableCount: valid.length });
      setSuccess(
        `${valid.length} email(s) ready` +
          (invalid.length ? ` · ${invalid.length} invalid ignored` : '')
      );
      return;
    }

    if (audienceMode === 'address' && !address.trim()) {
      setError('Enter an area address to preview recipients.');
      return;
    }
    if (audienceMode === 'technician' && !selectedTech) {
      setError('Pick a technician to preview recipients.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/notifications/clients/preview`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(targetBody()),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to generate preview');
      }
      const recs = result.data.recipients || [];
      setRecipients(recs);
      setSelectedIds(new Set(recs.map((r) => r.id))); // default: all selected
      setPreviewMeta(result.data);
      if (recs.length === 0) {
        setSuccess('No active customers matched the target.');
      } else {
        setSuccess(
          `${result.data.totalTargeted} customer(s) targeted` +
            (result.data.radiusMiles ? ` within ${result.data.radiusMiles} mi` : ' (all customers)') +
            ` · ${result.data.emailableCount} with email.`
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForSend = () => {
    if (audienceMode === 'address' && !address.trim()) return 'Enter an area address.';
    if (audienceMode === 'technician' && !selectedTech) return 'Pick a technician.';
    if (isManual) {
      const anyValid =
        recipients.length > 0 ? selectedRecipients.length > 0 : manualValid.length > 0;
      if (!anyValid) return 'Enter at least one valid email address.';
    }
    if (!subject.trim() || !message.trim()) return 'Subject and message are required.';
    if (recipients.length > 0 && selectedRecipients.length === 0)
      return 'Select at least one recipient.';
    if ((ctaText.trim() && !ctaUrl.trim()) || (!ctaText.trim() && ctaUrl.trim()))
      return 'Provide both the button label and the button link, or neither.';
    return null;
  };

  const handleSend = async () => {
    resetMessages();
    const validationError = validateForSend();
    if (validationError) {
      setError(validationError);
      return;
    }

    // If a preview was run, send only the checked recipients; otherwise all targeted.
    const hasSelection = recipients.length > 0;
    // Manual: the checked emails if previewed, else every valid parsed email.
    const manualSelected = isManual
      ? hasSelection
        ? Array.from(selectedIds)
        : manualValid
      : null;
    const count = isManual
      ? manualSelected.length
      : hasSelection
        ? selectedEmailable
        : previewMeta?.emailableCount ?? null;
    const audience = isManual
      ? `${manualSelected.length} manually-entered email(s)`
      : audienceMode === 'address'
        ? `customers within ${radiusMiles} miles of "${address.trim()}"`
        : audienceMode === 'technician'
          ? `customers within ${radiusMiles} miles of ${`${selectedTech.firstName || ''} ${selectedTech.lastName || ''}`.trim()}'s service area`
          : 'ALL active customers';
    if (
      !window.confirm(
        `Send "${subject.trim()}" to ${audience}${count != null ? ` (~${count} emails)` : ''}? ` +
          `Sending runs in the background and can't be undone.`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/notifications/clients/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...(isManual ? { manualEmails: manualSelected } : targetBody()),
          subject,
          message,
          bccMode,
          ...(ctaText.trim() && ctaUrl.trim() ? { ctaText: ctaText.trim(), ctaUrl: ctaUrl.trim() } : {}),
          ...(!isManual && hasSelection ? { includeClientIds: Array.from(selectedIds) } : {}),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send the campaign');
      }
      if (result.started) {
        // Campaign runs in the background; results land in the History tab.
        setSuccess(
          result.message ||
            'Outreach started. Sending runs in the background — check the History tab for results.'
        );
        setActiveTab(1);
        fetchCampaigns();
      } else {
        setSendResults(result.data);
        setSuccess(
          `Email: ${result.data.totalSent} sent` +
            (result.data.totalFailed ? `, ${result.data.totalFailed} failed` : '') +
            ` · ${result.data.area}`
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetMessages();
    setRecipients([]);
    setSelectedIds(new Set());
    setPreviewMeta(null);
    setSendResults(null);
    setPreviewCampaign(null);
    setActiveTab(0);
    onClose();
  };

  const sendStatusChip = (recipient) => {
    if (!sendResults) return null;
    // Match on client id for DB audiences, or on email for the manual list.
    const r = sendResults.results?.find(
      (x) => (x.clientId != null && x.clientId === recipient.id) || x.recipient === recipient.email
    );
    if (!r) return null;
    return (
      <Chip
        size="small"
        sx={{ ml: 0.5 }}
        label={r.success ? 'sent' : 'failed'}
        color={r.success ? 'success' : 'error'}
        variant="outlined"
      />
    );
  };

  const renderCompose = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={audienceMode}
            onChange={(_e, mode) => {
              if (!mode) return;
              setAudienceMode(mode);
              setRecipients([]);
              setSelectedIds(new Set());
              setPreviewMeta(null);
            }}
          >
            <ToggleButton value="all">
              <GroupsIcon fontSize="small" sx={{ mr: 0.5 }} /> All customers
            </ToggleButton>
            <ToggleButton value="address">
              <LocationIcon fontSize="small" sx={{ mr: 0.5 }} /> Near an address
            </ToggleButton>
            <ToggleButton value="technician">
              <PersonIcon fontSize="small" sx={{ mr: 0.5 }} /> Near a technician
            </ToggleButton>
            <ToggleButton value="manual">
              <EmailIcon fontSize="small" sx={{ mr: 0.5 }} /> Manual list
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {isManual && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Email addresses"
              placeholder="Paste or type emails, separated by commas, spaces, or new lines"
              value={manualInput}
              onChange={(e) => {
                setManualInput(e.target.value);
                setRecipients([]);
                setSelectedIds(new Set());
                setPreviewMeta(null);
              }}
              helperText={
                manualInput.trim()
                  ? `${manualValid.length} valid${manualInvalid.length ? ` · ${manualInvalid.length} invalid (ignored)` : ''}`
                  : 'These are not personalized — [CLIENT_NAME] becomes “there”.'
              }
            />
          </Grid>
        )}

        {audienceMode === 'address' && (
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Area address"
              placeholder="e.g. Kissimmee, FL"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              helperText="Center of the search radius (customer addresses on file are used)"
            />
          </Grid>
        )}

        {audienceMode === 'technician' && (
          <Grid item xs={12} sm={8}>
            <Autocomplete
              options={techOptions}
              loading={techLoading}
              value={selectedTech}
              onChange={(_e, v) => {
                setSelectedTech(v);
                setRecipients([]);
                setSelectedIds(new Set());
                setPreviewMeta(null);
              }}
              inputValue={techInput}
              onInputChange={(_e, v) => setTechInput(v)}
              getOptionLabel={(o) =>
                `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email || ''
              }
              isOptionEqualToValue={(o, v) => o.userId === v.userId}
              renderOption={(props, o) => (
                <li {...props} key={o.userId}>
                  <Box>
                    {`${o.firstName || ''} ${o.lastName || ''}`.trim()}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {typeof o.residentialAddress === 'string'
                        ? o.residentialAddress
                        : o.residentialAddress
                          ? [o.residentialAddress.street, o.residentialAddress.city]
                              .filter(Boolean)
                              .join(', ')
                          : 'No address on file'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Technician"
                  helperText="Their home address becomes the center of the radius"
                />
              )}
            />
          </Grid>
        )}

        {limitToArea && (
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Radius (miles)"
              type="number"
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(e.target.value)}
              inputProps={{ min: 1, max: 200 }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            helperText="[CLIENT_NAME] works here too — replaced with each customer's first name."
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Email message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            helperText="Use [CLIENT_NAME] to insert the customer's first name. Sent with your company branding."
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Button label (optional)"
            placeholder="e.g. Book a cleaning"
            value={ctaText}
            onChange={(e) => setCtaText(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            label="Button link (optional)"
            placeholder="https://…"
            value={ctaUrl}
            onChange={(e) => setCtaUrl(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Send a copy to the company inbox (BCC)
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={bccMode}
            onChange={(_e, mode) => {
              if (mode) setBccMode(mode);
            }}
          >
            <ToggleButton value="off">No BCC</ToggleButton>
            <ToggleButton value="first">First email only</ToggleButton>
            <ToggleButton value="all">Every email</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={handlePreview}
          disabled={loading}
        >
          Preview Recipients
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={loading}
        >
          {loading ? 'Working...' : 'Send Campaign'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ my: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ my: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {recipients.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {limitToArea ? <LocationIcon fontSize="small" /> : <GroupsIcon fontSize="small" />}
            Targeted customers — {selectedRecipients.length} of {recipients.length} selected
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={toggleAll}
                      inputProps={{ 'aria-label': 'Select all recipients' }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  {limitToArea && <TableCell align="right">Distance (mi)</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    selected={selectedIds.has(r.id)}
                    onClick={() => toggleRecipient(r.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleRecipient(r.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {`${r.firstName || ''} ${r.lastName || ''}`.trim() || '—'}
                      {sendStatusChip(r)}
                    </TableCell>
                    <TableCell>
                      {r.hasEmail ? (
                        r.email
                      ) : (
                        <Chip size="small" label="No email" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    {limitToArea && (
                      <TableCell align="right">
                        {r.distance != null ? Number(r.distance).toFixed(1) : '—'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );

  const renderHistory = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon fontSize="small" />
          Past campaigns
        </Typography>
        <Button size="small" onClick={fetchCampaigns} disabled={historyLoading}>
          Refresh
        </Button>
      </Box>

      {historyError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError('')}>
          {historyError}
        </Alert>
      )}

      {historyLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : campaigns.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No campaigns have been sent yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Results</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(c.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{c.subject || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <div>{c.area || 'All customers'}</div>
                    {c.radiusMiles != null && (
                      <Typography variant="caption" color="text.secondary">
                        {c.radiusMiles} mi radius
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {c.emailSent} of {c.totalRecipients} sent
                      {c.emailFailed ? `, ${c.emailFailed} failed` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setPreviewCampaign(c)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderPreviewDialog = () => (
    <Dialog
      open={Boolean(previewCampaign)}
      onClose={() => setPreviewCampaign(null)}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        Email as sent
        {previewCampaign?.subject && (
          <Typography variant="body2" color="text.secondary">
            Subject: {previewCampaign.subject}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {previewCampaign?.emailHtml ? (
          <Box
            component="iframe"
            title="email-preview"
            srcDoc={previewCampaign.emailHtml}
            sx={{ width: '100%', height: 480, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No stored content for this campaign.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewCampaign(null)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon color="primary" />
          <Typography variant="h6" component="span">
            Customer Outreach
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Email your customer base — everyone, customers near an area, or a manual email list
        </Typography>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} sx={{ mt: 1 }}>
          <Tab label="Compose" />
          <Tab label="History" />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers>
        {activeTab === 0 ? renderCompose() : renderHistory()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>

      {renderPreviewDialog()}
    </Dialog>
  );
};

export default ClientOutreachDialog;
